import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { useParty } from "../hooks/useParty";
import { usePartyInvites } from "../hooks/usePartyInvites";
import { useCancelPartyInvite } from "../hooks/useCancelPartyInvite";
import { useQueueParty } from "../hooks/useQueueParty";
import { useLeaveParty } from "../hooks/useLeaveParty";
import { useKickPartyMember } from "../hooks/useKickPartyMember";
import { InvitePicker } from "../components/InvitePicker";
import { useDropInCourtState } from "../hooks/useDropInCourtState";
import { useDropInSession } from "../hooks/useDropInSession";
import { formatInviteExpiry } from "../lib/formatInviteExpiry";
import {
  activeDropInAuthorizationExpiresAt,
  dropInSessionSnapshotIsAuthoritative,
} from "../lib/activeDropInAuthorization";
import {
  createEligibilityFocusRequest,
  PartyEligibilityPanel,
  type EligibilityFocusRequest,
} from "../components/PartyEligibilityPanel";
import {
  partyPlacementConflictNotice,
  type PartyPlacementNotice,
} from "../lib/partyPlacementConflict";

function SentInviteRow({
  invite,
  onCancel,
  pending,
}: {
  invite: PartyInviteResponse;
  onCancel: (inviteId: string) => void;
  pending: boolean;
}) {
  return (
    <Paper component="li" variant="outlined" sx={{ p: 1.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" } }}
      >
        <Typography sx={{ minWidth: 0, flex: 1, fontWeight: 700 }}>
          {(() => {
            const n = invite.invitee_display_name?.trim();
            return !n || n === invite.invitee_user_id ? "A player" : n;
          })()}
        </Typography>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Chip
            size="small"
            variant="outlined"
            label="Pending"
            sx={{ alignSelf: "flex-start" }}
          />
          <Typography
            component="time"
            variant="caption"
            color="text.secondary"
            dateTime={invite.expires_at}
          >
            Held until {formatInviteExpiry(invite.expires_at)}
          </Typography>
        </Stack>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={() => onCancel(invite.invite_id)}
          disabled={pending}
        >
          Cancel
        </Button>
      </Stack>
    </Paper>
  );
}

/**
 * Party detail (/dropin/party/:partyId).
 *
 * The leader/member view: the party status + a member COUNT (never raw
 * user_ids), outgoing pending invites (with Cancel), the checked-in player
 * picker, and the code-share fallback. The leader follows the facility's
 * assignment mode or disbands the party; a plain member can leave. All derived
 * state (membership, who is the leader) comes from the server party object.
 * A leader with remaining members leaves and transfers the party; only a sole
 * leader sees the disband action. Nothing is fabricated.
 *
 * PARTY-PERSISTENCE + INVITE-PICKER REWORK (Task 6, 2026-07-02):
 *   - Every current member's invite mechanism is `InvitePicker` — a
 *     recent+search picker over the checked-in roster (`GET
 *     /queue/party/{id}/invite-candidates`), server-capped and server-excluded
 *     (members / pending invitees / the caller). This REPLACES the old
 *     client-fetched, unbounded "Checked in now" section (`useCheckedInPlayers`
 *     + `dropinApi.listCheckedInPlayers`, retired — did not scale to a facility
 *     with hundreds of checked-in players).
 *   - The party CODE moved out of being the prominent, primary "share this"
 *     card at the top of the page into a small de-emphasized party-code
 *     secondary control below the picker — still visible to every member (not
 *     leader-gated; sharing the code was never a leader-only capability).
 *   - The former social-friends section is removed. It exposed unchecked
 *     players and created invites the recipient could not accept. The picker
 *     is the sole invite path, and the server already excludes the caller,
 *     current members, and pending invitees.
 */
export function PartyDetailPage() {
  const { partyId = "" } = useParams<{ partyId: string }>();
  const navigate = useNavigate();

  const partyQuery = useParty(partyId);
  const party = partyQuery.data ?? null;
  const eligibility = party?.eligibility;
  const hasCanonicalRoster = Array.isArray(eligibility?.members);
  const isLeader = hasCanonicalRoster && party?.viewer.role === "leader";
  const isMember =
    hasCanonicalRoster &&
    (party?.viewer.role === "leader" || party?.viewer.role === "member");
  const canInvite = hasCanonicalRoster && party?.viewer.can_invite === true;
  const canLeave = hasCanonicalRoster && party?.viewer.can_leave === true;
  const canRemoveMembers =
    hasCanonicalRoster && party?.viewer.can_remove_members === true;
  const canPlace = hasCanonicalRoster && party?.viewer.can_place === true;
  const invitesQuery = usePartyInvites(partyId, canRemoveMembers);
  const partyFacilityId = party?.facility_id ?? "";
  const sessionQuery = useDropInSession(partyFacilityId, {
    refreshMode: "expiry",
  });
  const activeAuthorizationExpiresAt = activeDropInAuthorizationExpiresAt(
    sessionQuery.data,
    partyFacilityId,
  );
  const sessionAuthorizationIsFresh =
    partyQuery.isSuccess &&
    partyQuery.isFetchedAfterMount &&
    partyQuery.error === null &&
    dropInSessionSnapshotIsAuthoritative(sessionQuery) &&
    activeAuthorizationExpiresAt !== null;
  const { isActorRevisionPending, isBoardRevisionPending } =
    useDropInCourtState(partyFacilityId, 0, {
      enabled: sessionAuthorizationIsFresh,
      authorizationExpiresAt: sessionAuthorizationIsFresh
        ? activeAuthorizationExpiresAt
        : null,
    });

  const cancelInvite = useCancelPartyInvite(partyId);
  const queueParty = useQueueParty(partyId, party?.facility_id);
  const leaveParty = useLeaveParty(party?.facility_id);
  const kickMember = useKickPartyMember(partyId);

  const [actionError, setActionError] = useState<PartyPlacementNotice | null>(
    null,
  );
  const [kickError, setKickError] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [eligibilityFocus, setEligibilityFocus] =
    useState<EligibilityFocusRequest | null>(null);
  const [partyRecoveryFacilityId, setPartyRecoveryFacilityId] = useState<
    string | null
  >(null);
  const focusSequence = useRef(0);
  const partyRecoveryRef = useRef<HTMLDivElement>(null);

  const partyIsOpen = party?.status === "open";
  const leaderExitDisbands = isLeader && party?.leader_exit_disbands === true;
  const hasAvailablePartySlots = (party?.capacity.available ?? 0) > 0;
  const partySnapshotCurrent =
    partyQuery.isSuccess &&
    partyQuery.isFetchedAfterMount &&
    !partyQuery.isFetching &&
    partyQuery.error === null &&
    !isActorRevisionPending;
  const partyCourtBoardPending =
    partySnapshotCurrent &&
    isBoardRevisionPending &&
    eligibility?.action === "choose_court";
  const invitesSnapshotCurrent =
    invitesQuery.isSuccess &&
    invitesQuery.isFetchedAfterMount &&
    !invitesQuery.isFetching &&
    invitesQuery.error === null;
  const sentInvites =
    isLeader && invitesSnapshotCurrent ? (invitesQuery.data ?? []) : [];

  useEffect(() => {
    if (isLeader || !isMember || !party?.entry_id || !partySnapshotCurrent) {
      return;
    }
    navigate(`/dropin/${party.facility_id}/queue/${party.entry_id}`, {
      replace: true,
    });
  }, [isLeader, isMember, navigate, party, partySnapshotCurrent]);

  useEffect(() => {
    if (
      partyRecoveryFacilityId === null ||
      eligibilityFocus?.target !== "recovery"
    ) {
      return;
    }
    partyRecoveryRef.current?.focus();
    partyRecoveryRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [eligibilityFocus, partyRecoveryFacilityId]);

  function handleCancel(inviteId: string) {
    if (!partySnapshotCurrent || !invitesSnapshotCurrent) return;
    cancelInvite.mutate(inviteId);
  }

  function handleKick(targetMemberId: string) {
    if (!partyIsOpen || !partySnapshotCurrent || !canRemoveMembers) return;
    setKickError(null);
    kickMember.mutate(targetMemberId, {
      onError: () =>
        setKickError("We couldn't remove that member. Please try again."),
    });
  }

  function handleQueue() {
    if (
      !party ||
      !partySnapshotCurrent ||
      !canPlace ||
      !eligibility?.leader_can_place ||
      (eligibility.action !== "join_auto_queue" &&
        eligibility.action !== "join_staff_queue")
    )
      return;
    setActionError(null);
    queueParty.mutate(
      { mode: "normal" },
      {
        onSuccess: (entry) => {
          navigate(`/dropin/${party.facility_id}/queue/${entry.entry_id}`);
        },
        onError: async (err) => {
          if (isApiError(err) && err.status === 409) {
            const conflictNotice = partyPlacementConflictNotice(err);
            const previous = party.eligibility;
            const refreshed = await partyQuery.refetch();
            const partyDisappeared =
              (refreshed.isSuccess && refreshed.data == null) ||
              (isApiError(refreshed.error) && refreshed.error.status === 404);
            const next = refreshed.isSuccess
              ? refreshed.data?.eligibility
              : partyDisappeared
                ? null
                : previous;
            setEligibilityFocus(
              createEligibilityFocusRequest(
                ++focusSequence.current,
                previous,
                next,
              ),
            );
            if (partyDisappeared) {
              setPartyRecoveryFacilityId(party.facility_id);
              setActionError(conflictNotice);
            } else if (refreshed.isSuccess) {
              setActionError(conflictNotice);
            } else {
              setActionError({
                ...conflictNotice,
                message: `${conflictNotice.message} We couldn't refresh party status.`,
              });
            }
            return;
          }
          setActionError({
            message: "We couldn't queue the party. Please try again.",
          });
        },
      },
    );
  }

  function handleLeave() {
    if (!party || !partyIsOpen || !partySnapshotCurrent || !canLeave) return;
    setActionError(null);
    leaveParty.mutate(partyId, {
      onSuccess: () => {
        navigate(`/dropin/${party.facility_id}/session`);
      },
      onError: () =>
        setActionError({
          message: "We couldn't leave the party. Please try again.",
        }),
    });
  }

  async function handleCopyCode() {
    if (!party) return;
    try {
      await navigator.clipboard?.writeText(party.code);
      setCodeCopied(true);
    } catch {
      /* clipboard unavailable — leave the code visible to copy manually */
    }
  }

  // ---- honest loading + access-denied states -----------------------------
  if (party === null && partyQuery.isLoading) {
    return (
      <Page>
        <Stack spacing={2}>
          <Typography component="h1" variant="h4">
            Your party
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            role="status"
            aria-live="polite"
            sx={{ alignItems: "center" }}
          >
            <CircularProgress size={18} />
            <Typography color="text.secondary">Loading your party…</Typography>
          </Stack>
        </Stack>
      </Page>
    );
  }

  if (partyRecoveryFacilityId !== null) {
    return (
      <Page>
        <Stack spacing={2}>
          <Typography component="h1" variant="h4">
            Your party
          </Typography>
          <Alert
            ref={partyRecoveryRef}
            severity="warning"
            role="status"
            tabIndex={-1}
            data-conflict-code={actionError?.conflictCode}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography>This party is no longer active.</Typography>
              <Button
                component={Link}
                to={`/dropin/${partyRecoveryFacilityId}/session`}
                variant="outlined"
                size="small"
              >
                Back to drop-in
              </Button>
            </Stack>
          </Alert>
        </Stack>
      </Page>
    );
  }

  if (party === null) {
    return (
      <Page>
        <Stack spacing={0.5}>
          <Typography component="h1" variant="h4">
            Your party
          </Typography>
          <Typography color="text.secondary">
            You don&apos;t have access to this party.
          </Typography>
        </Stack>
      </Page>
    );
  }

  // Terminal state: a disbanded party has no members and no actions left. Show
  // a dead-end-free terminal view (not the actionable invite/queue/disband UI,
  // whose controls would all fail on a gone party) + a link back to the drop-in
  // hub so the leader can start a fresh party in one tap.
  if (party.status === "disbanded") {
    return (
      <Page>
        <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
          <Box>
            <Typography component="h1" variant="h4">
              Your party
            </Typography>
            <Typography color="text.secondary">
              This party was disbanded.
            </Typography>
          </Box>
          <Button
            component={Link}
            to={`/dropin/${party.facility_id}/session`}
            variant="contained"
            size="small"
          >
            Back to drop-in
          </Button>
        </Stack>
      </Page>
    );
  }

  return (
    <Page>
      <Stack
        spacing={3}
        sx={{ "& .MuiButton-root.Mui-disabled": { pointerEvents: "auto" } }}
      >
        <Box>
          <Typography component="h1" variant="h4">
            Your party
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Invite checked-in players, then queue together for a court.
          </Typography>
        </Box>

        {partyQuery.error !== null ? (
          <Alert severity="error">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "center" } }}
            >
              <Typography sx={{ flex: 1 }}>
                We couldn&apos;t refresh your party status. Actions are paused
                until it is current.
              </Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={() => void partyQuery.refetch()}
              >
                Retry party status
              </Button>
            </Stack>
          </Alert>
        ) : null}

        <Stack spacing={2}>
          {/* Party facts and capacity are one server-authored snapshot. Pending
            invitations reserve slots, so clients never infer availability from
            whichever invite rows this caller may be allowed to see. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography color="text.secondary">Status</Typography>
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={party.status}
              />
            </Stack>
            <Stack
              component="dl"
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              role="group"
              aria-label="Party capacity"
              sx={{ m: 0, mt: 2 }}
            >
              <CapacityFact label="Accepted" value={party.capacity.accepted} />
              <CapacityFact label="Pending" value={party.capacity.pending} />
              <CapacityFact
                label="Available"
                value={party.capacity.available}
              />
              <CapacityFact label="Maximum" value={party.capacity.maximum} />
            </Stack>
          </Paper>

          {actionError ? (
            <Alert
              severity="error"
              data-conflict-code={actionError.conflictCode}
            >
              {actionError.message}
            </Alert>
          ) : null}
          {kickError ? <Alert severity="error">{kickError}</Alert> : null}
          <PartyEligibilityPanel
            eligibility={party.eligibility}
            canPlaceParty={canPlace}
            canRemoveMembers={canRemoveMembers}
            canViewActivity={canLeave}
            actionsDisabled={!partySnapshotCurrent}
            showPartyAction={!partyCourtBoardPending}
            actionPending={queueParty.isPending}
            removePending={kickMember.isPending}
            activityHref={
              party.entry_id
                ? `/dropin/${party.facility_id}/queue/${party.entry_id}`
                : undefined
            }
            focusRequest={eligibilityFocus}
            onPlaceParty={handleQueue}
            onRemoveMember={partyIsOpen ? handleKick : undefined}
          />
          {partyCourtBoardPending ? (
            <Typography
              variant="body2"
              color="text.secondary"
              role="status"
              aria-label="Court status"
              aria-live="polite"
            >
              Refreshing court status. Court selection will return when the
              latest update is ready.
            </Typography>
          ) : null}

          {/* The facility-scoped picker is the sole invite mechanism. The queue
            service authorizes every current party member to invite. */}
          {partyIsOpen && canInvite && hasAvailablePartySlots ? (
            <InvitePicker
              key={partyId}
              partyId={partyId}
              actionsDisabled={!partySnapshotCurrent}
            />
          ) : partyIsOpen && canInvite ? (
            <Typography color="text.secondary" role="status">
              All {party.capacity.maximum} slots are accepted or pending.
            </Typography>
          ) : null}

          {/* A full party cannot accept a code join, so do not expose a share
            action that can only end in PARTY_FULL. */}
          {partyIsOpen && canInvite && hasAvailablePartySlots ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ alignItems: { sm: "center" } }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Party code
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 800,
                      letterSpacing: "0.12em",
                    }}
                  >
                    {party.code}
                  </Typography>
                </Box>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={handleCopyCode}
                >
                  {codeCopied ? "Copied!" : "Copy code"}
                </Button>
                <Box
                  aria-live="polite"
                  sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    p: 0,
                    m: "-1px",
                    overflow: "hidden",
                    clip: "rect(0 0 0 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  {codeCopied ? "Copied!" : ""}
                </Box>
              </Stack>
            </Paper>
          ) : null}

          {/* Outgoing pending invites (omit the section entirely when none). */}
          {isLeader && sentInvites.length > 0 ? (
            <Box component="section" aria-labelledby="sec-invited">
              <Typography variant="overline" color="primary.main">
                Sent
              </Typography>
              <Typography component="h2" variant="h5" id="sec-invited">
                Pending invitations
              </Typography>
              <Stack
                component="ul"
                spacing={1}
                sx={{ mt: 1.5, p: 0, listStyle: "none" }}
              >
                {sentInvites.map((sent) => (
                  <SentInviteRow
                    key={sent.invite_id}
                    invite={sent}
                    onCancel={handleCancel}
                    pending={
                      cancelInvite.isPending ||
                      !partySnapshotCurrent ||
                      !invitesSnapshotCurrent
                    }
                  />
                ))}
              </Stack>
            </Box>
          ) : null}

          {/* Party exit remains separate from Queue's placement capability. */}
          {partyIsOpen && canLeave ? (
            <Box component="section" aria-labelledby="sec-party-actions">
              <Typography variant="overline" color="text.secondary">
                Membership
              </Typography>
              <Typography component="h2" variant="h5" id="sec-party-actions">
                Party controls
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                {isLeader ? (
                  <Button
                    type="button"
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={handleLeave}
                    disabled={leaveParty.isPending || !partySnapshotCurrent}
                  >
                    {leaderExitDisbands ? "Disband party" : "Leave party"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    color="error"
                    variant="outlined"
                    size="small"
                    onClick={handleLeave}
                    disabled={leaveParty.isPending || !partySnapshotCurrent}
                  >
                    Leave party
                  </Button>
                )}
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </Stack>
    </Page>
  );
}

function CapacityFact({ label, value }: { label: string; value: number }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1,
        bgcolor: "action.hover",
        borderRadius: 1,
      }}
    >
      <Typography component="dt" variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="h6" sx={{ m: 0, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}
